import React, { useState, useMemo, memo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Collapse,
  useTheme,
} from '@mui/material';
import {
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  ExpandMore as ExpandMoreIcon,
  Build as ToolIcon,
} from '@mui/icons-material';
import { MessageWithObservability } from '../../../types';
import { groupObservabilityEvents, getGroupSummary, getToolLabel, getToolDisplay, formatDuration, ObservabilityGroup } from '../utils/observabilityGrouping';
import MessageEditMode from './MessageEditMode';
import MessageViewMode from './MessageViewMode';
import { useViewport } from '../../../shared/hooks/useViewport';

interface ChatMessageProps {
  message: MessageWithObservability;
  onEdit?: (messageId: string, newContent: string) => void;
  onAddAsFinding?: (messageContent: string) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = memo(({ message, onEdit, onAddAsFinding }) => {
  const theme = useTheme();
  const isUser = message.role === 'user';
  const { isPhone } = useViewport();
  // A phone has no room for side gutters: the bubble takes the row, the avatar and padding shrink.
  const [avatarSize, rowPx, assistantWidth] = isPhone ? [28, 1, '100%'] : [36, 2, '90%'];
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>({});
  const [expandedTools, setExpandedTools] = useState<Record<string, boolean>>({});

  const observabilityGroups = useMemo(
    () => groupObservabilityEvents(message.observability || []),
    [message.observability]
  );
  const hasObservability = observabilityGroups.length > 0;

  const toggleGroup = (index: number) => {
    setExpandedGroups(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleTool = (key: string) => {
    setExpandedTools(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    if (editContent.trim() && onEdit) {
      onEdit(message.id, editContent.trim());
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditContent(message.content);
    setIsEditing(false);
  };

  const getAgentColor = () => {
    if (isUser) return 'primary.main';
    return theme.palette.primary.main;
  };

  const getAgentBgColor = () => {
    if (isUser) return 'primary.main';
    return theme.palette.chat.assistantBg;
  };

  const getAgentTextColor = () => {
    if (isUser) return 'primary.contrastText';
    return theme.palette.chat.assistantText;
  };

  const renderObservabilityGroup = (group: ObservabilityGroup, groupIdx: number) => {
    if (group.toolSummaries.length === 0) return null;
    return (
      <Box key={groupIdx} sx={{ mb: groupIdx < observabilityGroups.length - 1 ? 0.5 : 0 }}>
        <Box
          onClick={() => toggleGroup(groupIdx)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            cursor: 'pointer',
            opacity: 0.6,
            '&:hover': { opacity: 0.85, bgcolor: 'action.hover' },
          }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 14,
              color: 'text.disabled',
              transform: expandedGroups[groupIdx] ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ flex: 1, fontSize: '0.75rem' }}
          >
            {getGroupSummary(group)}
          </Typography>
        </Box>

        <Collapse in={expandedGroups[groupIdx]}>
          <Box
            sx={{
              mt: 0.25,
              ml: 3,
              pl: 1.5,
              py: 0.25,
              borderLeft: '1px solid',
              borderColor: 'divider',
            }}
          >
            {group.toolSummaries.map((toolSummary, idx) => {
              const duration = toolSummary.total_duration_ms
                ? formatDuration(toolSummary.total_duration_ms)
                : null;
              const toolKey = `${groupIdx}-${toolSummary.tool_name}-${idx}`;
              const isToolExpanded = expandedTools[toolKey] || false;
              // context_summary is primary; TOOL_LABELS is fallback
              const primaryLabel = getToolDisplay(toolSummary.tool_name, toolSummary.context_summary);
              const hasDetail = !!toolSummary.context_summary;
              return (
                <Box
                  key={toolKey}
                  sx={{ py: 0.25 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 0.75,
                      cursor: hasDetail ? 'pointer' : 'default',
                      '&:hover': hasDetail ? { opacity: 0.85 } : {},
                    }}
                    onClick={hasDetail ? () => toggleTool(toolKey) : undefined}
                  >
                    <ToolIcon sx={{ fontSize: 12, color: 'text.disabled', mt: 0.25 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.72rem' }}>
                        {primaryLabel}
                        {toolSummary.count > 1 && ` ×${toolSummary.count}`}
                        {duration && ` · ${duration}`}
                      </Typography>
                    </Box>
                    {hasDetail && (
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 12,
                          color: 'text.disabled',
                          transform: isToolExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                  {hasDetail && (
                    <Collapse in={isToolExpanded}>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{
                          fontSize: '0.68rem',
                          display: 'block',
                          opacity: 0.75,
                          fontFamily: 'monospace',
                          ml: 2.25,
                          mt: 0.25,
                          wordBreak: 'break-all',
                        }}
                      >
                        {getToolLabel(toolSummary.tool_name)}: {toolSummary.context_summary}
                      </Typography>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <>
      {!isUser && hasObservability && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            mb: 1,
            px: rowPx,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              width: assistantWidth,
              maxWidth: assistantWidth,
              gap: 1,
            }}
          >
            <Box sx={{ width: avatarSize, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {observabilityGroups.map(renderObservabilityGroup)}
            </Box>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 2,
          px: rowPx,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isUser ? 'row-reverse' : 'row',
            alignItems: 'flex-start',
            maxWidth: isUser && !isPhone ? '70%' : assistantWidth,
            width: isUser ? 'auto' : assistantWidth,
            minWidth: 0,
            gap: 1,
          }}
        >
          <Avatar sx={{ bgcolor: getAgentColor(), width: avatarSize, height: avatarSize }}>
          {isUser ? <PersonIcon /> : <PsychologyIcon />}
        </Avatar>

        <Paper
          elevation={2}
          sx={{
            p: isPhone ? 1.5 : 2,
            bgcolor: getAgentBgColor(),
            color: getAgentTextColor(),
            borderRadius: 2,
            borderTopLeftRadius: isUser ? 2 : 0.5,
            borderTopRightRadius: isUser ? 0.5 : 2,
            position: 'relative',
            flex: (isEditing || !isUser) ? 1 : 'initial',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {isEditing ? (
            <MessageEditMode
              editContent={editContent}
              isUser={isUser}
              onContentChange={setEditContent}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          ) : (
            <MessageViewMode
              content={message.content}
              timestamp={message.timestamp}
              isUser={isUser}
              tokenUsage={message.token_usage}
              blocks={message.blocks}
              onEdit={onEdit ? () => setIsEditing(true) : undefined}
              onAddAsFinding={onAddAsFinding}
            />
          )}
        </Paper>
        </Box>
      </Box>
    </>
  );
}, (prevProps, nextProps) => {
  return prevProps.message.id === nextProps.message.id
    && prevProps.message.content === nextProps.message.content
    && prevProps.message.observability === nextProps.message.observability
    && prevProps.message.token_usage === nextProps.message.token_usage
    && prevProps.message.blocks === nextProps.message.blocks
    && prevProps.onEdit === nextProps.onEdit
    && prevProps.onAddAsFinding === nextProps.onAddAsFinding;
});

ChatMessage.displayName = 'ChatMessage';

export default ChatMessage;
